import Feature from "ol/Feature";
import VectorSource from "ol/source/Vector";
import Point from "ol/geom/Point";

import { beginOfBudgetYears, isString, isNumeric, createDate } from "@/common/utils";
import { EUserGroup } from "@/components/shared/types";
import { IMapLayerSource, IMapLayer, IModifiedFeature, IMovedFeature, 
         ISplitResult, ICutFeature } from "@/components/map/types";
import { User } from "@/components/User";
import { getLanguage } from '@/common/filters';
import store from '@/store';

const BUDGET_YEARS = beginOfBudgetYears();
// use MAX_RESOLUTION_4_EDITING to trigger the loader
// but we do real loading when resolution comes down to 4.777314267158508
// that is about 1:9,000 
// in this way we want to "alway" show those features that are being edited 
export const MAX_RESOLUTION_4_EDITING = 4.777314267158508 / 2; //base is 1:9000
export const MAX_RESOLUTION_4_EDITING_X = 256;

interface IVisibility {
  budgetYearIndex0?: boolean;
  budgetYearIndex1?: boolean;
  budgetYearIndex2?: boolean;
  farmerRegistered?: boolean;
  farmerNotRegistered?: boolean;
}

function doaeStyle(feature, resolution) {
  let STATE = feature.get('STATE');
  if (STATE=='Delete') {
    return null; // hide "deleted" feature
  }
  //feature property visibilities is set in IMapLayer.setVisibility() as defined in MAP_LAYERS
  let v:IVisibility = feature.get('visibilities'); // set on addfeature event, and reset in setFarmlandVisibility()
  let fssn = feature.get('fssn_fk');
  let createdOn: Date = feature.get('fssn_ts'); //create_ts
  let BudgetYearIndex:number = createdOn ? (BudgetYear() - BudgetYear(createdOn)) : 0;

  // default values
  let basemapType = feature.NATIVE_SOURCE.NATIVE_LAYERS[0].SOFTOO_MAPS[0].map.BaseMapType;
  let visible:boolean = v.farmerNotRegistered;
  let lineColor:string, fillColor:string;
  let zIndex: number;
  if (!!STATE) {
    // new or modified feature, displayed on topmost
    if (basemapType=='map') {
      lineColor = '#000000';
      fillColor = 'rgba(0,0,0,0.12)';
    } else {
      lineColor = '#ffffff';
      fillColor = 'rgba(255,255,255,0.2)';
    }
    zIndex = 4;
  } else if (resolution<=MAX_RESOLUTION_4_EDITING) {
    // only show original features for predefined large scale
    if (!fssn) {
      lineColor = '#ff0000'; 
      fillColor = 'rgba(255,0,0,0.12)';
      zIndex = 0;
    }
    else {
      if (BudgetYearIndex===0) {
        visible = v.budgetYearIndex0;
        lineColor = '#33cc00'; //green
        fillColor = 'rgba(52,204,0,0.12)';
        zIndex = 3;
      } else if (BudgetYearIndex===1) {
        visible = v.budgetYearIndex1;
        lineColor = '#ffa500'; //orange
        fillColor = 'rgba(255,165,0,0.12)';
        zIndex = 2;
      } else {
        visible = v.budgetYearIndex2;
        lineColor = '#ff00ff'; //pink
        fillColor = 'rgba(155,0,255,0.12)';
        zIndex = 1;
      }
    }
  } else {
    return null;
  }
  if (!visible) {
    return null; // hidden
  }
  let geom = feature.getGeometry();
  let width = resolution<=MAX_RESOLUTION_4_EDITING ? 1.5 : 2.0;
  let style;
  if (geom instanceof Point) {
    style = {
      //label: { text:t, rotation:r, fill: {color: color} },
      points: [{
        circle: {
          radius: 4,
          fill: { color: fillColor },
          stroke: { color: lineColor, width: width }
        }
      }],
      zIndex: zIndex
    };
  } else {
    let r = feature.get('angle') || 0;
    r = r * Math.PI / 180;
    //let t = (Math.round(parseFloat('0'+feature.get('area_sqm')) * 10) / 10) + '';
    //let t = feature.get('area_rai') + '-' + feature.get('area_ngan') + '-' + feature.get('area_sqwah');
    let lang = getLanguage();
    let title = feature.get('title_'+lang);
    let name = feature.get('name_'+lang);
    let surname = feature.get('surname_'+lang);
    title = ''; //20181221: requirements as not to show title
    let t = (title ? title : '') + (name ? name : '') + (surname ? (' ' + surname) : '');
    feature.set('SOFTOO_LABEL', {text:t, rotation:r});
    style = {
      label: { text:t, rotation:r, fill: {color: lineColor} },
      strokes: [{
        color: lineColor,
        width: width
      }],
      zIndex: zIndex
    };
  }
  return style;
}

function commonStyle(feature, resolution) {
  let STATE = feature.get('STATE');
  if (STATE=='Delete') {
    return null; // hide "deleted" feature
  }
  let basemapType = feature.NATIVE_SOURCE.NATIVE_LAYERS[0].SOFTOO_MAPS[0].map.BaseMapType;
  //feature property visibilities is set IMapLayer.setVisibility() as defined in MAP_LAYERS
  let v:IVisibility = feature.get('visibilities'); // set on addfeature event, and reset in setFarmlandVisibility()
  let fssn = feature.get('fssn_fk');
  // default values
  let visible:boolean = v.farmerNotRegistered;
  let lineColor:string, fillColor:string;
  let zIndex: number;
  if (!!STATE) {
    // new or modified feature, displayed on topmost
    if (basemapType=='map') {
      lineColor = '#000000';
      fillColor = 'rgba(0,0,0,0.12)';
    } else {
      lineColor = '#ffffff';
      fillColor = 'rgba(255,255,255,0.2)';
    }
    zIndex = 4;
  } else if (resolution<=MAX_RESOLUTION_4_EDITING) {
    if (fssn) {
      visible = v.farmerRegistered;
      lineColor = '#33cc00'; //green
      fillColor = 'rgba(42,204,0,0.12)';
      zIndex = 1;
    } else {
      lineColor = '#ff0000'; 
      fillColor = 'rgba(255,0,0,0.12)';
      zIndex = 0;
    }
  } else {
    return null;
  }
  if (!visible) {
    return null; // hidden
  }
  let geom = feature.getGeometry();
  let width = resolution<=MAX_RESOLUTION_4_EDITING ? 1.5 : 2.0;
  let style;
  if (geom instanceof Point) {
    style = {
      //label: { text:t, rotation:r, fill: {color: color} },
      points: [{
        circle: {
          radius: 4,
          fill: { color: fillColor },
          stroke: { color: lineColor, width: width }
        }
      }],
      zIndex: zIndex
    };
  } else {
    let r = feature.get('angle') || 0;
    r = r * Math.PI / 180;
    //let t = (Math.round(parseFloat('0'+feature.get('area_sqm')) * 10) / 10) + '';
    //let t = feature.get('area_rai') + '-' + feature.get('area_ngan') + '-' + feature.get('area_sqwah');
    let lang = getLanguage();
    let title = feature.get('title_'+lang);
    let name = feature.get('name_'+lang);
    let surname = feature.get('surname_'+lang);
    title = ''; //20181221: requirements as not to show title
    let t = (title ? title : '') + (name ? name : '') + (surname ? (' ' + surname) : '');
    feature.set('SOFTOO_LABEL', {text:t, rotation:r});
    style = {
      label: { text:t, rotation:r, fill: {color: lineColor} },
      strokes: [{
        color: lineColor,
        width: width
      }],
      zIndex: zIndex
    };
  }
  return style;
}

export function BudgetYear(d?:Date | string | number) : number {
  if (d && !(d instanceof Date)) {
    d = createDate(d);
  }
  let today = (<Date>d) || new Date();
  let yyyy = today.getFullYear();
  let beginOfBudgetYear = new Date(yyyy,9,1,0,0,0,0);
  if (today >= beginOfBudgetYear) {
    yyyy++;
  }
  return yyyy;
}

function setFarmlandVisibility(mapSources:IMapLayerSource[], visibilities:IVisibility) {
  mapSources.forEach((ms) => {
    if (ms.nativeLayer) {
      let source = ms.nativeLayer.getSource();
      if (source['getFeatures']) {
        let v:IVisibility = source['visibilities'] = Object.assign({
          budgetYearIndex0: true,
          budgetYearIndex1: true,
          budgetYearIndex2: true,
          farmerRegistered: true,
          farmerNotRegistered: true
        }, source['visibilities'] || {}, visibilities);
        source['getFeatures']().forEach((f) => {
          f.set('visibilities', v);
        });
        if (v.budgetYearIndex0 || v.budgetYearIndex1 || v.budgetYearIndex2 || 
            v.farmerNotRegistered || v.farmerRegistered) {
          ms.nativeLayer.setVisible(true);
        } else {
          ms.nativeLayer.setVisible(false);
        }
      } else {
        ms.nativeLayer.setVisible(ms.visible);
      }
    }
  });
}

function calcRaiNganSqwah(land:Feature) {
  let area:number = land.get('area_sqm');
  if (area || area===0) {
    let rai:number, ngan:number, sqwah:number;
    area = area / 1600.0; //to rai
    rai = Math.trunc(area);
    area = (area - rai) * 4.0; //to ngan
    ngan = Math.trunc(area);
    area = (area - ngan) * 100.0; //to sqwah
    sqwah = Math.round(area);
    if (sqwah>=100) {
      sqwah -= 100;
      ngan += 1;
      if (ngan>=4) {
        ngan -= 1;
        rai += 1;
      }
    }
    land.set('area_rai', rai);
    land.set('area_ngan', ngan);
    land.set('area_sqwah', sqwah);
  }
}

function addNewFarmland(land:Feature) {
  if (land.get('labelStyle')) {
    land.unset('labelStyle');
  }
  calcRaiNganSqwah(land);
}

export function selectSplitTargets() {
  // use this function to select all polygons to be split
  // such that RAOT requires to select all lands having the same num_app
  if (!this.hasTargets && this.$map.map.HighlightedFeature) {
    this.targets = [this.$map.map.HighlightedFeature];
    this.$map.selectFeature(this.$map.map.HighlightedFeature);
  }
}

export function afterMoved(moved:IModifiedFeature) {
  console.log('moved feature' + moved.modified['ol_uid']);
}

export function afterModified(modified:IModifiedFeature[]) {
  try {
    /* to do
    let checkPolygon:Feature;
    let polygon;
    for (let i=0; i<modified.length; i++) {
      polygon = modified[i].modified;
      if (this.$map.isPolygon(polygon)) {
        break;
      } else {
        polygon = null;
      }
    }
    if (polygon && store && store.getters) {
      let user:User = store.getters.User;
      let intersects = await user.requestResource('gis/intersects/forests', 'POST', {
        wkt: this.$map.FeatureToWKT(polygon, {
          dataProjection:'EPSG:4326',
          featureProjection:'EPSG:3857'
        }),
        srid: 4326
      });
    }*/
    modified.forEach((result) => {
      let f = result.modified;
      let orgArea = f.get('originalAreaSqm');
      let newArea = f.get('area_sqm');
      if (newArea>orgArea) {
        f.set('AreaModifiedMode','enlarged');
      } else if (newArea<orgArea) {
        f.set('AreaModifiedMode','reduced');
      } else {
        f.unset('AreaModifiedMode');
      }
    });
    return true;
  } catch (error) {
    throw(error);
  }
}

export function afterSplitted(split:ISplitResult) {
  let source:VectorSource;
  let results = (split.splits || []).concat(split.splits2 || []);
  if (results.length>0) {
    let hasUndoTarget:boolean = false;
    split.targets.forEach((target,index) => {
      source = target['NATIVE_SOURCE'];
      if (source) {
        this.$map.addUndo(target, 'Delete', index>0);
        hasUndoTarget = true;
      }
    });
    results.forEach((result,index) => {
      source = result.modified['NATIVE_SOURCE'];
      if (source) {
        this.$map.addUndo(result.modified, 'Insert', hasUndoTarget || index>0); //append
        source.addFeature(result.modified);
        result.modified.set('AreaModifiedMode','enlarged');
      }
    });
  }
}

export function afterCut(cut:ICutFeature) {
  //reserved
  cut.modified.set('AreaModifiedMode','reduced');
}

export function afterDeleted(modified:IModifiedFeature[]) {
  //reserved
}

const MAP_SOURCES: {[key:string] : IMapLayerSource } = {
  wms_farmland_frd_fy0: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      //'TILED': true,
      'LAYERS': 'gisagro:farmland_frd_fy0'
    },
    filterOptions: ['aag','crop','deed','year','area'],
    //useGwc: true, //GeoWebCache requires SRS not CRS in API
    minResolution: MAX_RESOLUTION_4_EDITING,
    visible: true, //display it after added to map
    zIndex: 904
  },
  wms_farmland_frd_fy1: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      //'TILED': true,
      'LAYERS': 'gisagro:farmland_frd_fy1'
    },
    filterOptions: ['aag','crop','deed','year','area'],
    //useGwc: true, //GeoWebCache requires SRS not CRS in API
    minResolution: MAX_RESOLUTION_4_EDITING,
    visible: true, //display it after added to map
    zIndex: 903
  },
  wms_farmland_frd_fy2: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      //'TILED': true,
      'LAYERS': 'gisagro:farmland_frd_fy2'
    },
    filterOptions: ['aag','crop','deed','year','area'],
    //useGwc: true, //GeoWebCache requires SRS not CRS in API
    minResolution: MAX_RESOLUTION_4_EDITING,
    visible: true, //display it after added to map
    zIndex: 902
  },
  wms_farmland_fnr: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      //,'CQL_FILTER': 'aag3_fk is not null', //removed this condition on 20190318
      //'TILED': true,
      'LAYERS': 'gisagro:farmland_fnr'
    },
    filterOptions: ['aag','crop','deed','year','area'],
    //useGwc: true, //GeoWebCache requires SRS not CRS in API
    minResolution: MAX_RESOLUTION_4_EDITING,
    visible: true, //display it after added to map
    zIndex: 901
  },
  wms_farmland_frd: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      //'TILED': true,
      'LAYERS': 'gisagro:farmland_frd'
    },
    filterOptions: ['aag','crop','deed','year','area'],
    //useGwc: true, //GeoWebCache requires SRS not CRS in API
    minResolution: MAX_RESOLUTION_4_EDITING,
    visible: true, //display it after added to map
    zIndex: 902
  },
  wfs_farmland_doae: {
    service: 'WFS',
    serverType: 'geoserver',
    featureType: 'gisagro:farmland_edit',
    canEdit: true,
    geometryType: 'Polygon',
    strategy: '', //always ue bbox
    maxResolution: MAX_RESOLUTION_4_EDITING * MAX_RESOLUTION_4_EDITING_X, // threshhold to make feature visible, such as for those beinging editted
    maxEditResolution: MAX_RESOLUTION_4_EDITING, // threshhold for loading feature from server
    visible: true, //display it after added to map
    style: doaeStyle,
    zIndexOffset: 910,
    events: {
      addfeature: (event) => {
        let source = event.target || this;
        let feature = event.feature;
        source.visibilities = source.visibilities || {
          budgetYearIndex0: true,
          budgetYearIndex1: true,
          budgetYearIndex2: true,
          farmerNotRegistered: true
        };
        feature.set('visibilities', source.visibilities);
        if (!feature.getId()) {
          addNewFarmland(feature);
        }
      },
      changefeature: function(event) {
        // fired on feature.set();
        //let source = event.target || this;
        let feature = event.feature;
        calcRaiNganSqwah(feature);
      },
      removefeature: (event) => {
        let e =event;
      },
      clear: (event) => {
        let e = event;
      }
    }
  },
  wfs_farmland_point_doae: {
    service: 'WFS',
    serverType: 'geoserver',
    featureType: 'gisagro:farmland_point_edit',
    canEdit: true,
    geometryType: 'Point',
    strategy: '', //always ue bbox
    maxResolution: MAX_RESOLUTION_4_EDITING * MAX_RESOLUTION_4_EDITING_X,
    maxEditResolution: MAX_RESOLUTION_4_EDITING, // threshhold for loading feature from server
    visible: true, //display it after added to map
    style: doaeStyle,
    zIndexOffset: 911,
    events: {
      addfeature: (event) => {
        let source = event.target || this;
        let feature = event.feature;
        source.visibilities = source.visibilities || {
          budgetYearIndex0: true,
          budgetYearIndex1: true,
          budgetYearIndex2: true,
          farmerNotRegistered: true
        };
        feature.set('visibilities', source.visibilities);
        if (!feature.getId()) {
          addNewFarmland(feature);
        }
      },
      changefeature: function(event) {
        let feature = event.feature;
        calcRaiNganSqwah(feature);
      },
      removefeature: (event) => {
        let e =event;
      },
      clear: (event) => {
        let e = event;
      }
    }
  },
  wfs_farmland: {
    service: 'WFS',
    serverType: 'geoserver',
    featureType: 'gisagro:farmland_edit',
    canEdit: true,
    geometryType: 'Polygon',
    strategy: '', //always ue bbox
    maxResolution: MAX_RESOLUTION_4_EDITING * MAX_RESOLUTION_4_EDITING_X,
    maxEditResolution: MAX_RESOLUTION_4_EDITING, // threshhold for loading feature from server
    visible: true, //display it after added to map
    style: commonStyle,
    zIndexOffset: 910,
    events: {
      addfeature: (event) => {
        let source = event.target || this;
        let feature = event.feature;
        source.visibilities = source.visibilities || {
          farmerRegistered: true,
          farmerNotRegistered: true
        };
        feature.set('visibilities', source.visibilities);if (!feature.getId()) {
          addNewFarmland(feature);
        }
      },
      changefeature: function(event) {
        let feature = event.feature;
        calcRaiNganSqwah(feature);
      }
    }
  },
  wfs_farmland_point: {
    service: 'WFS',
    serverType: 'geoserver',
    featureType: 'gisagro:farmland_point_edit',
    canEdit: true,
    geometryType: 'Point',
    strategy: '', //always ue bbox
    maxResolution: MAX_RESOLUTION_4_EDITING * MAX_RESOLUTION_4_EDITING_X,
    maxEditResolution: MAX_RESOLUTION_4_EDITING, // threshhold for loading feature from server
    visible: true, //display it after added to map
    style: commonStyle,
    zIndexOffset: 911,
    events: {
      addfeature: (event) => {
        let source = event.target || this;
        let feature = event.feature;
        source.visibilities = source.visibilities || {
          farmerRegistered: true,
          farmerNotRegistered: true
        };
        feature.set('visibilities', source.visibilities);
      },
      changefeature: function(event) {
        // fired on feature.set();
        let source = event.target || this;
        let features = source.getFeatures();
        let feature = event.feature;
      }
    }
  },
  wms_wildlife_sanctuary: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:wildlife_sanctuary', 
      'TILED': true
    },
    visible: false, //display it after added to map
    zIndex: 20
  },
  wms_non_hunting: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:non_hunting', 
      'TILED': true
    },
    visible: false, //display it after added to map
    zIndex: 29
  },
  wms_national_forest: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:national_forest', 
      'TILED': true
    },
    visible: false, //display it after added to map
    zIndex: 12
  },
  wms_forest_park: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:forest_park', 
      'TILED': true
    },
    visible: false, //display it after added to map
    zIndex: 13
  },
  wms_national_park: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:national_park', 
      'TILED': true
    },
    visible: false, //display it after added to map
    zIndex: 11
  },
  wms_mangrove_forest: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:mangrove_forest', 
      'TILED': true
    },
    visible: false, //display it after added to map
    zIndex: 0
  }
};

export const MAP_LAYERS: IMapLayer[] = [{
    // for DOAE users
    title: 'mlFarmland', // these name should be defined in i18n files
    grantToUser: function(user:User) : boolean {
      return user.isDoae;
    },
    mapSources: [
        //dont change following order
        MAP_SOURCES.wms_farmland_fnr,
        MAP_SOURCES.wms_farmland_frd_fy2,
        MAP_SOURCES.wms_farmland_frd_fy1,
        MAP_SOURCES.wms_farmland_frd_fy0
      ],
    visible: true,
    children: [{
        title: 'mlFarmlandRIPBY',
        //replacer: [BUDGET_YEARS.present.getFullYear() + 543],
        visible: true,
        onVisibilityChange: function(visible) {
          setFarmlandVisibility(this.mapSources, {
            budgetYearIndex0: visible
          });
        },
        mapSources: [
            MAP_SOURCES.wms_farmland_frd_fy0,
            MAP_SOURCES.wfs_farmland_doae,
            MAP_SOURCES.wfs_farmland_point_doae
          ],
        style: {
          border: '1px solid #33cc00'                                                       
        }
      },{
        title: 'mlFarmlandRIBY',
        replacer: [BUDGET_YEARS.present.getFullYear() + 542],
        visible: true,
        onVisibilityChange: function(visible) {
          setFarmlandVisibility(this.mapSources, {
            budgetYearIndex1: visible
          });
        },
        mapSources: [
            MAP_SOURCES.wms_farmland_frd_fy1,
            MAP_SOURCES.wfs_farmland_doae,
            MAP_SOURCES.wfs_farmland_point_doae
          ],
        style: {
          border : '1px solid #ffa500' 
        }
      },{
        title: 'mlFarmlandRBBY',
        replacer: [BUDGET_YEARS.present.getFullYear() + 542],
        visible: true,
        onVisibilityChange: function(visible) {
          setFarmlandVisibility(this.mapSources, {
            budgetYearIndex2: visible
          });
        },
        mapSources: [
            MAP_SOURCES.wms_farmland_frd_fy2,
            MAP_SOURCES.wfs_farmland_doae,
            MAP_SOURCES.wfs_farmland_point_doae
          ],
        style: {
          border : '1px solid #ff00ff'
        }
      },{
        title: 'mlFarmlandNR',
        visible: true,
        onVisibilityChange: function(visible) {
          setFarmlandVisibility(this.mapSources, {
            farmerNotRegistered: visible
          });
        },
        mapSources: [
            MAP_SOURCES.wms_farmland_fnr,
            MAP_SOURCES.wfs_farmland_doae,
            MAP_SOURCES.wfs_farmland_point_doae
          ],
        style: {
          border : '1px solid #ff0000'
        }
      }]
  },{
    // for none-DOAE users
    title: 'mlFarmland', 
    grantToUser: function(user:User) :boolean {
      return user.isOtherOrg || user.isFarmer;
    },
    mapSources: [
      /*MAP_SOURCES.wms_farmland_frd,
      MAP_SOURCES.wms_farmland_fnr*/
    ],
    visible: true,
    children: [{
        title: 'mlFarmlandR',
        visible: true,
        onVisibilityChange: function(visible) {
          setFarmlandVisibility(this.mapSources, {
            farmerRegistered: visible
          });
        },
        mapSources: [
            MAP_SOURCES.wfs_farmland_point,
            MAP_SOURCES.wfs_farmland,
            MAP_SOURCES.wms_farmland_frd
          ],
        style: {
          border: '1px solid #33cc00'
        }
      },
      { title: 'mlFarmlandNR',
        visible: true,
        onVisibilityChange: function(visible) {
          setFarmlandVisibility(this.mapSources, {
            farmerNotRegistered: visible
          });
        },
        mapSources: [
            MAP_SOURCES.wfs_farmland_point,
            MAP_SOURCES.wfs_farmland,
            MAP_SOURCES.wms_farmland_fnr
          ],
        style: {
          border : '1px solid #ff0000'
        }
      }]
  },{
    title: 'adminArea',
    mapSources: [{
        service: 'WMS',
        serverType: 'geoserver',
        params: {
          'LAYERS': 'gisagro:admin_boundaries', //workspace_name:layer_name
          'TILED': true
        },
        //useGwc: false, //GeoWebCache requires SRS not CRS in API
        visible: true, //display it after added to map
        zIndex: 1000
      }],
    visible: true,
    children: [{
        title: 'ประเทศ',
        image: '/static/images/legend-boundary-country.svg'
      },{
        title: 'จังหวัด',
        image: '/static/images/legend-boundary-changwat.svg'
      },{
        title: 'อำเภอ',
        image: '/static/images/legend-boundary-amphoe.svg'
      },{
        title: 'ตำบล',
        image: '/static/images/legend-boundary-tambon.svg'
      },{
        title: 'เส้นชายทะเล',
        image: '/static/images/legend-boundary-shoreline.svg'
      }]
  },{
    title: 'mlFromAllSystems',
    grantToUser: function(user:User) :boolean {
      return user.isDoae || user.isGistda;
    },
    mapSources: [{
        service: 'WMS',
        serverType: 'geoserver',
        url: 'http://ssmap.doae.go.th:8080/geoserver/WMS_TEST/wms',
        params: {
          'LAYERS': 'parcel', //workspace_name:layer_name
          'TILED': true
        },
        //useGwc: true, //GeoWebCache requires SRS not CRS in API
        //minResolution: MAX_RESOLUTION_4_EDITING * MAX_RESOLUTION_4_EDITING_X,
        visible: false, //display it after added to map
        zIndex: 100
      }],
    children: [{
        title: 'GISagro',
        style: {background:'#00ff00'}
      },{
        title: 'QGIS',
        style: {background:'#00ffff'}
      },{
        title: 'DOAE',
        style: {background:'#ff9d00'}
      },{
        title: 'FAARMIS',
        style: {background:'#fa3000'}
      }]
  },{
    title: 'เขตป่า',
    mapSources: [
      MAP_SOURCES.wms_mangrove_forest,
      MAP_SOURCES.wms_national_forest,
      MAP_SOURCES.wms_national_park,
      MAP_SOURCES.wms_forest_park,
      MAP_SOURCES.wms_wildlife_sanctuary,
      MAP_SOURCES.wms_non_hunting
    ],
    visible: false,
    children: [{
      title: 'เขตรักษาพันธุ์สัตว์ป่า',
      visible: false,
      mapSources: [
        MAP_SOURCES.wms_wildlife_sanctuary
        ],
      style: {
        border: '1px solid #99A3A4',
        background: 'rgba(153,163,164,0.8)'                                                   
      }
    },{
      title: 'เขตห้ามล่าสัตว์ป่า',
      visible: false,
      mapSources: [
        MAP_SOURCES.wms_non_hunting
        ],
      style: {
        border: '1px solid #E74C3C',
        background: 'rgba(231,76,60,0.8)'                                                   
      }
    },{
      title: 'อุทยานแห่งชาติ',
      visible: false,
      mapSources: [
        MAP_SOURCES.wms_national_park
        ],
      style: {
        border: '1px solid #2ECC71',
        background: 'rgba(46,204,113,0.8)'                                                   
      }
    },{
      title: 'วนอุทยาน',
      visible: false,
      mapSources: [
        MAP_SOURCES.wms_forest_park
        ],
      style: {
        border: '1px solid #D6F09E',
        background: 'rgba(214,240,158,0.8)'                                                   
      }
    },{
      title: 'ป่าสงวนแห่งชาติ',
      visible: false,
      mapSources: [
        MAP_SOURCES.wms_national_forest
        ],
      style: {
        border: '1px solid #239B56',
        background: 'rgba(35,155,86,0.8)'                                                   
      }
    },{
      title: 'ป่าชายเลน',
      visible: false,
      mapSources: [
        MAP_SOURCES.wms_mangrove_forest
        ],
      style: {
        border: '1px solid #F7DC6F',
        background: 'rgba(247,220,111,0.8)'                                                   
      }
    }]
  }];

export const PRINT_LAYERS: IMapLayer[] = [{
    // for DOAE users
    title: 'mlFarmland', // these name should be defined in i18n files
    grantToUser: function(user:User) : boolean {
      return user.isDoae;
    },
    mapSources: [ 
        MAP_SOURCES.wms_farmland_frd_fy0,
        MAP_SOURCES.wms_farmland_frd_fy1,
        MAP_SOURCES.wms_farmland_frd_fy2,
        MAP_SOURCES.wms_farmland_fnr,
    ],
    showCtrls: false,
    visible: true,
    children: [{
        title: 'mlFarmlandRIBY',
        replacer: [BUDGET_YEARS.present.getFullYear() + 543],
        visible: true,
        showCtrls: false,
        mapSources: [
            MAP_SOURCES.wms_farmland_frd_fy0,
            MAP_SOURCES.wfs_farmland_doae
          ],
        style: {
          border: '1px solid #33cc00'                                                       
        }
      },{
        title: 'mlFarmlandRIBY',
        replacer: [BUDGET_YEARS.present.getFullYear() + 542],
        visible: true,
        showCtrls: false,
        mapSources: [
            MAP_SOURCES.wms_farmland_frd_fy1,
            MAP_SOURCES.wfs_farmland_doae
          ],
        style: {
          border : '1px solid #ffa500' 
        }
      },{
        title: 'mlFarmlandRBBY',
        replacer: [BUDGET_YEARS.present.getFullYear() + 542],
        visible: true,
        showCtrls: false,
        mapSources: [
            MAP_SOURCES.wms_farmland_frd_fy2,
            MAP_SOURCES.wfs_farmland_doae
          ],
        style: {
          border : '1px solid #ff00ff'
        }
      },{
        title: 'mlFarmlandNR',
        visible: true,
        showCtrls: false,
        mapSources: [
            MAP_SOURCES.wms_farmland_fnr,
            MAP_SOURCES.wfs_farmland_doae
          ],
        style: {
          border : '1px solid #ff0000'
        }
      }]
  },{
    // for none-DOAE users
    title: 'mlFarmland', 
    grantToUser: function(user:User) :boolean {
      return user.isOtherOrg || user.isFarmer;
    },
    mapSources: [
      MAP_SOURCES.wms_farmland_frd,
      MAP_SOURCES.wms_farmland_fnr
    ],
    showCtrls: false,
    visible: true,
    children: [{
        title: 'mlFarmlandR',
        showCtrls: false,
        visible: true,
        mapSources: [
            MAP_SOURCES.wfs_farmland,
            MAP_SOURCES.wms_farmland_frd
          ],
        style: {
          border: '1px solid #33cc00'
        }
      },
      { title: 'mlFarmlandNR',
        showCtrls: false,
        visible: true,
        mapSources: [
            MAP_SOURCES.wfs_farmland,
            MAP_SOURCES.wms_farmland_fnr
          ],
        style: {
          border : '1px solid #ff0000'
        }
      }]
  },{
    title: 'adminArea',
    mapSources: [{
        service: 'WMS',
        serverType: 'geoserver',
        params: {
          'LAYERS': 'gisagro:admin_boundaries', //workspace_name:layer_name
          'TILED': true
        },
        //useGwc: false, //GeoWebCache requires SRS not CRS in API
        visible: true, //display it after added to map
        zIndex: 1000
      }],
    showCtrls: false,
    visible: true,
    children: [{
        title: 'ประเทศ',
        image: '/static/images/legend-boundary-country.svg'
      },{
        title: 'จังหวัด',
        image: '/static/images/legend-boundary-changwat.svg'
      },{
        title: 'อำเภอ',
        image: '/static/images/legend-boundary-amphoe.svg'
      },{
        title: 'ตำบล',
        image: '/static/images/legend-boundary-tambon.svg'
      },{
        title: 'เส้นชายทะเล',
        image: '/static/images/legend-boundary-shoreline.svg'
      }]
  }];

export function getAuthorizedLayers (
  user: User, 
  LAYERS?: IMapLayer[],
  defaults?: boolean) : IMapLayer[] {
  let layers: IMapLayer[] = [];
  LAYERS = LAYERS || MAP_LAYERS;
  LAYERS.forEach((m: IMapLayer) => {
    let m_ = Object.assign({}, m); // clone it
    let g = m_.grantToUser ? m_.grantToUser(user) : (defaults!==false);
    if (g) {
      let mss:IMapLayerSource[];
      if (!user.canEdit && m_.mapSources && m_.mapSources.length>0) {
        mss = [];
        m_.mapSources.forEach((ms) => {
          if (!(ms.service.match(/^WFS$|^Vector$/i) && ms.canEdit)) {
            //ignore those layers that can be editted
            if (ms.minResolution!==undefined) {
              delete ms.minResolution;
            }
            mss.push(ms);
          }
        });
        m_.mapSources = mss;
      }
      layers.push(m_);
      if (g && m_.children && m_.children.length>0) {
        m_.children = getAuthorizedLayers(user, m_.children, g);
        if (m_.children.length==0) {
          delete m_.children;
        }
      } else if (g && m_.legend && m_.legend.length>0) {
        m_.legend = getAuthorizedLayers(user, m_.legend, g);
        if (m_.legend.length==0) {
          delete m_.legend;
        }
      }
    }
  }, this);
  return layers;
}

export default MAP_LAYERS;
